import { FC } from 'react';
import S from './EventCard.module.scss';
import { AttendeeTicket } from 'src/types/types';
import { Gallery } from 'src/redesign/components/Gallery';
import { Text } from 'src/redesign/components/Text';
import calendarIcon from 'src/redesign/assets/calendar-icon.svg';
import locationIcon from 'src/redesign/assets/location-icon.svg';
import timeIcon from 'src/redesign/assets/time-icon.svg';
import { ArrowRight } from 'wix-ui-icons-common';
import { attendeeEventsSidebarActions } from 'src/redesign/features/AttendeeEventsSidebar/store/reducer';
import { useAppDispatch } from 'src/hooks';
import { RedesignButton } from '@components/RedesignButton';
import moment from 'moment';

type EventCardProps = {
  key?: string;
  ticketInfo: AttendeeTicket;
};

const DEFAULT_IMAGE = 'http://cdn.ticketspot.io/no-image-default.png';

export const EventCard: FC<EventCardProps> = ({ ticketInfo, key }) => {
  const { event } = ticketInfo;
  const actions = attendeeEventsSidebarActions;
  const dispatch = useAppDispatch();

  // Prepare image for gallery
  const images = [event.image_url || DEFAULT_IMAGE];
  if (event.secondary_image_urls?.length > 0) {
    images.push(...event.secondary_image_urls);
  }

  // Format date and time from start_date and end_date
  // Example format for date: "25 Feb - 1 Mar 2024"
  // Example format for time: "09:00 PM - 11:00 PM"

  const startDate = moment(event.start_date);
  const endDate = moment(event.end_date);

  // Format date part
  let displayDate = '';

  // If start and end dates are on the same day
  if (startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD')) {
    displayDate = startDate.format('D MMM YYYY');
  }
  // If start and end dates are in the same month and year
  else if (startDate.format('MMM YYYY') === endDate.format('MMM YYYY')) {
    displayDate = `${startDate.format('D')} - ${endDate.format('D MMM YYYY')}`;
  }
  // If start and end dates are in different months but same year
  else if (startDate.format('YYYY') === endDate.format('YYYY')) {
    displayDate = `${startDate.format('D MMM')} - ${endDate.format('D MMM YYYY')}`;
  }
  // If start and end dates are in different years
  else {
    displayDate = `${startDate.format('D MMM YYYY')} - ${endDate.format('D MMM YYYY')}`;
  }

  // Format time part
  const startTime = startDate.format('h:mm A');
  const endTime = endDate.format('h:mm A');
  const displayTime = `${startTime} - ${endTime}`;

  return (
    <div className={S.eventCard} key={key}>
      <Gallery images={images} />
      <div className={S.ticketInfoContainer}>
        <Text className={S.ticketTitle} variation="body-large" color="black-400">
          {event.title}
        </Text>
        <div className={S.informationBlock}>
          <div className={S.dateInfo}>
            <img src={calendarIcon} alt="" />
            <Text>{displayDate}</Text>
          </div>
          {displayTime && (
            <div className={S.timeInfo}>
              <img src={timeIcon} alt="" />
              <Text>{displayTime}</Text>
            </div>
          )}
          {event.location && (
            <div className={S.positionInfo}>
              <img src={locationIcon} alt="" /> {event.location}
            </div>
          )}
        </div>
        <RedesignButton
          onClick={() => {
            dispatch(actions.setCurrentEvent(ticketInfo));
            dispatch(actions.showAttendeeEventsSidebar());
          }}
          className={S.detailsButton}
          height={43}
        >
          <Text regular color="black-400">
            View Tickets
          </Text>
          <ArrowRight style={{ stroke: 'black' }} />
        </RedesignButton>
      </div>
    </div>
  );
};
